import { format } from 'date-fns';
import { toZonedTime, formatInTimeZone } from 'date-fns-tz';

/**
 * Get the user's timezone from their profile or browser
 */
export const getUserTimezone = (profileTimezone?: string | null): string => {
  if (profileTimezone) {
    return profileTimezone;
  }
  
  // Fallback to browser timezone
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (error) {
    console.error('Error getting browser timezone:', error);
    return 'America/New_York'; // Default fallback
  }
};

/**
 * Format a date in the user's timezone
 */
export const formatInUserTimezone = (
  date: Date | string,
  formatStr: string,
  timezone?: string
): string => {
  const tz = timezone || getUserTimezone();
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  try {
    return formatInTimeZone(dateObj, tz, formatStr);
  } catch (error) {
    console.error('Error formatting date in timezone:', error);
    return format(dateObj, formatStr);
  }
};

/**
 * Get timezone abbreviation (e.g., EST, PST, GMT)
 */
export const getTimezoneAbbreviation = (
  date: Date | string = new Date(),
  timezone?: string
): string => {
  const tz = timezone || getUserTimezone();
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  try {
    // Convert to zoned time first
    const zonedDate = toZonedTime(dateObj, tz);
    
    // Use Intl to get the short timezone name
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      timeZoneName: 'short',
    });
    
    const parts = formatter.formatToParts(zonedDate);
    const timeZonePart = parts.find(part => part.type === 'timeZoneName');
    
    return timeZonePart?.value || tz;
  } catch (error) {
    console.error('Error getting timezone abbreviation:', error);
    return tz;
  }
};

/**
 * Get a list of common timezones for dropdown
 */
export const getCommonTimezones = (): { value: string; label: string }[] => {
  return [
    { value: 'America/New_York', label: 'Eastern Time (ET)' },
    { value: 'America/Chicago', label: 'Central Time (CT)' },
    { value: 'America/Denver', label: 'Mountain Time (MT)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
    { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
    { value: 'Pacific/Honolulu', label: 'Hawaii Time (HT)' },
    { value: 'Europe/London', label: 'London (GMT/BST)' },
    { value: 'Europe/Paris', label: 'Paris (CET/CEST)' },
    { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
    { value: 'Asia/Shanghai', label: 'Shanghai (CST)' },
    { value: 'Asia/Dubai', label: 'Dubai (GST)' },
    { value: 'Australia/Sydney', label: 'Sydney (AEDT/AEST)' },
  ];
};
